import type { ReactNode } from "react"
import { Link, usePage } from "@inertiajs/react"
import { Copy, ExternalLink, ImageIcon, MapPin, Pencil, Phone } from "lucide-react"
import { toast } from "sonner"

import { StaffLayout } from "@/components/layouts/staff-layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { StatusBadge } from "@/components/ui/status-badge"
import { formatDateTime } from "@/lib/formatters"
import { t } from "@/lib/i18n"
import { publicMenuState, publicMenuTone } from "@/lib/status-colors"
import { cn } from "@/lib/utils"
import { formatMinutes, weekdayShortName } from "@/features/schedule/schedule-time"

const tShow = (key: string, options?: Record<string, string | number>) =>
  t(`ui.react.pages.staff.location.manage.location.show.${key}`, options)

const tSection = (key: string) =>
  t(`ui.react.pages.staff.location.manage.location.sections.${key}`)

type LocationSummary = {
  name: string
  status: string
  statusLabel: string
  logoUrl: string | null
  description: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  state: string | null
  postalCode: string | null
  country: string | null
  fullAddress: string | null
  hasCoordinates: boolean
  supportPhone: string | null
  timeZone: string
  operationallySuspended: boolean
}

type FeatureName = { key: string; label: string }

type SchedulePeriod = { startsAt: number; endsAt: number }

/**
 * Consecutive days with identical hours, already collapsed server-side: the
 * grouping depends on nothing the browser knows, and the location's own time
 * zone is where "which day is this" gets decided (react.md rule 16).
 */
type ScheduleRun = { days: number[]; periods: SchedulePeriod[] }

type Props = {
  location: LocationSummary
  publicMenu: {
    available: boolean
    enabled: boolean
    slug: string | null
    url: string | null
    urlPrefix: string
    published: boolean
    suggestedSlug: string | null
  } | null
  features: { enabled: FeatureName[]; disabled: FeatureName[]; totalCount: number }
  businessHours: {
    enabled: boolean
    timeZone: string
    openNow: boolean
    nextOpeningAt: string | null
    runs: ScheduleRun[]
  }
  tips: { percentages: number[]; distribution: { waiters: number; shared: number } }
  taxes: { regimeLabel: string; pricesIncludeTax: boolean; rates: { name: string; rate: string }[] } | null
}

/**
 * A titled section on the page with its surface beneath it, and the one link
 * that edits it.
 *
 * Title on the paper rather than inside the card: a column of labelled data
 * reads as one document, where a stack of cards each carrying its own header
 * reads as a dashboard of unrelated widgets. This is the same rule DESIGN.md
 * fixes for tables, applied to the branch report.
 */
function Section({
  title,
  href,
  testId,
  children,
}: {
  title: string
  href: string
  testId: string
  children: ReactNode
}) {
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <h2 className="truncate text-base font-semibold tracking-tight">{title}</h2>
        <Link
          href={href}
          className="shrink-0 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          data-testid={testId}
        >
          {tShow("edit")}
        </Link>
      </div>
      <div className="rounded-xl border bg-card p-4 shadow-sm">{children}</div>
    </section>
  )
}

/** A value that has not been filled in yet, dimmed rather than hidden. */
function NotSet({ label }: { label?: string }) {
  return <span className="text-muted-foreground/60">{label ?? tShow("not_set")}</span>
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-3 border-t py-2 text-sm first:border-t-0 first:pt-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate">{children}</span>
    </div>
  )
}

/**
 * The branch as a diner meets it, plus whether it is taking orders right now.
 *
 * It leads because it is the question a manager opening this screen actually
 * has. Paper surface inside the page rather than a white card: the tonal
 * inversion says "this is the public side" without a label saying so.
 */
function BranchCard({
  location,
  businessHours,
}: {
  location: LocationSummary
  businessHours: Props["businessHours"]
}) {
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <div className="flex items-start gap-5">
        {location.logoUrl ? (
          <img
            src={location.logoUrl}
            alt={location.name}
            className="size-16 shrink-0 rounded-lg border object-cover"
            data-testid="branch-logo"
          />
        ) : (
          <div className="flex size-16 shrink-0 items-center justify-center rounded-lg border border-dashed text-muted-foreground">
            <ImageIcon className="size-5" />
          </div>
        )}

        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-lg font-semibold tracking-tight">{location.name}</h2>
            <OpenState businessHours={businessHours} />
          </div>

          {location.description ? (
            <p className="text-sm text-muted-foreground">{location.description}</p>
          ) : (
            <p className="text-sm text-muted-foreground/60">{tShow("no_description")}</p>
          )}

          <div className="flex flex-wrap gap-x-5 gap-y-1 pt-1 text-xs text-muted-foreground">
            {location.fullAddress && (
              <span className="flex items-center gap-1.5">
                <MapPin className="size-3.5" />
                {location.fullAddress}
              </span>
            )}
            {location.supportPhone && (
              <span className="flex items-center gap-1.5 tabular-nums">
                <Phone className="size-3.5" />
                {location.supportPhone}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * A disabled schedule imposes no restriction, so it is neither open nor closed —
 * saying "abierta ahora" for a branch with no hours configured would be a claim
 * the schedule is not making. `Schedule#satisfied_at?` returns true in that
 * case, which is why `enabled` is read first.
 */
function OpenState({ businessHours }: { businessHours: Props["businessHours"] }) {
  if (!businessHours.enabled) {
    return <Badge variant="outline">{tShow("no_schedule")}</Badge>
  }

  if (businessHours.openNow) {
    return <StatusBadge tone="available" label={tShow("open_now")} data-testid="branch-open-state" />
  }

  return (
    <span className="flex items-center gap-2">
      <StatusBadge tone="disabled" label={tShow("closed_now")} data-testid="branch-open-state" />
      {businessHours.nextOpeningAt && (
        <span className="text-xs text-muted-foreground">
          {tShow("opens_at", {
            when: formatDateTime(businessHours.nextOpeningAt, {
              timeZone: businessHours.timeZone,
              options: { weekday: "long", hour: "numeric", minute: "2-digit" },
            }),
          })}
        </span>
      )}
    </span>
  )
}

/**
 * Runs, not seven identical rows: "Lun – Jue 9:00 – 22:00" is how a person
 * writes a schedule, and four repetitions of the same line is how a screen
 * makes someone read it four times to learn one fact.
 */
function ScheduleRuns({ runs }: { runs: ScheduleRun[] }) {
  if (runs.length === 0) {
    return <p className="text-sm text-muted-foreground">{tShow("hours_empty")}</p>
  }

  return (
    <div data-testid="branch-hours">
      {runs.map((run) => (
        <div
          key={run.days.join("-")}
          className="grid grid-cols-[7rem_minmax(0,1fr)] gap-3 border-t py-2 text-sm first:border-t-0 first:pt-0"
        >
          <span className="text-muted-foreground">
            {run.days.length === 1
              ? weekdayShortName(run.days[0])
              : `${weekdayShortName(run.days[0])} – ${weekdayShortName(run.days[run.days.length - 1])}`}
          </span>
          {run.periods.length === 0 ? (
            <span className="text-muted-foreground">{tShow("hours_closed")}</span>
          ) : (
            <span className="space-x-2 tabular-nums">
              {run.periods.map((period) => (
                <span key={`${period.startsAt}-${period.endsAt}`}>
                  {formatMinutes(period.startsAt)} – {formatMinutes(period.endsAt)}
                </span>
              ))}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

export default function StaffLocationManageLocationShow() {
  const { location, publicMenu, features, businessHours, tips, taxes } = usePage<Props>().props

  const copyMenuUrl = () => {
    if (!publicMenu?.url) return

    void navigator.clipboard.writeText(publicMenu.url).then(() => toast.success(tShow("copied")))
  }

  return (
    <StaffLayout>
      <MainContainer size="md">
        <PageHeader
          title={tShow("page_title")}
          description={tShow("page_description")}
          actions={
            <>
              <Badge variant={location.status === "active" ? "default" : "outline"}>{location.statusLabel}</Badge>
              <Button asChild variant="outline">
                <Link href="/manage/location/edit" data-testid="branch-edit">
                  <Pencil />
                  {tShow("edit_branch")}
                </Link>
              </Button>
            </>
          }
        />

        {location.operationallySuspended ? (
          <p
            className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            data-testid="branch-suspended"
          >
            {tShow("suspended")}
          </p>
        ) : null}

        <BranchCard location={location} businessHours={businessHours} />

        <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
          <div className="space-y-6">
            {publicMenu ? (
              <Section
                title={tSection("public_menu_title")}
                href="/manage/location/public_menu"
                testId="branch-edit-public-menu"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <StatusBadge
                      tone={publicMenuTone(publicMenuState(publicMenu))}
                      label={tSection(`public_menu_state_${publicMenuState(publicMenu)}`)}
                    />
                    {publicMenu.url && (
                      <div className="flex gap-2">
                        <Button type="button" variant="ghost" size="icon-sm" onClick={copyMenuUrl} aria-label={tShow("copy")}>
                          <Copy />
                        </Button>
                        <Button asChild variant="outline" size="sm">
                          <a href={publicMenu.url} target="_blank" rel="noreferrer">
                            {tShow("public_menu_view")}
                            <ExternalLink />
                          </a>
                        </Button>
                      </div>
                    )}
                  </div>
                  {publicMenu.url ? (
                    <p className="truncate rounded-lg border bg-background p-3 text-sm font-medium" data-testid="branch-menu-url">
                      {publicMenu.urlPrefix}
                      {publicMenu.slug}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground">{tShow("public_menu_no_slug")}</p>
                  )}
                </div>
              </Section>
            ) : null}

            <Section
              title={t("ui.react.pages.staff.location.manage.business_hours.show.title")}
              href="/manage/location/business_hours"
              testId="branch-edit-hours"
            >
              <ScheduleRuns runs={businessHours.runs} />
              {!businessHours.enabled && (
                <p className="mt-3 border-t pt-3 text-xs text-muted-foreground">{tShow("hours_disabled_note")}</p>
              )}
            </Section>

            <Section
              title={tSection("features_title")}
              href="/manage/location/features"
              testId="branch-edit-features"
            >
              {features.totalCount === 0 ? (
                <p className="text-sm text-muted-foreground">{tShow("features_empty")}</p>
              ) : (
                <div className="space-y-4" data-testid="branch-features">
                  <FeatureList
                    label={tShow("features_enabled", { count: features.enabled.length })}
                    features={features.enabled}
                    enabled
                  />
                  <FeatureList
                    label={tShow("features_disabled", { count: features.disabled.length })}
                    features={features.disabled}
                  />
                </div>
              )}
            </Section>
          </div>

          <div className="space-y-6">
            <Section title={tSection("tips_title")} href="/manage/location/tips" testId="branch-edit-tips">
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">{tShow("tips_diner_sees")}</p>
                  <div className="flex flex-wrap gap-2" data-testid="branch-tip-options">
                    {tips.percentages.map((percentage) => (
                      <span
                        key={percentage}
                        className="rounded-md border px-2 py-1 text-xs font-medium tabular-nums"
                      >
                        {percentage === 0 ? tShow("tips_no_tip") : `${percentage}%`}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Two numbers in two boxes say nothing; the bar is read before it
                    is read. Neutral, not brand: this is a proportion, not identity. */}
                <div className="space-y-2 border-t pt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5">
                      <span className="size-2 rounded-full bg-primary" />
                      {tShow("tips_waiters")}
                    </span>
                    <span className="font-semibold tabular-nums">{tips.distribution.waiters}%</span>
                  </div>
                  <div className="flex h-2 overflow-hidden rounded-full bg-muted" aria-hidden="true">
                    <span className="bg-primary" style={{ width: `${tips.distribution.waiters}%` }} />
                    <span className="bg-muted-foreground/40" style={{ width: `${tips.distribution.shared}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5">
                      <span className="size-2 rounded-full bg-muted-foreground/40" />
                      {tShow("tips_shared")}
                    </span>
                    <span className="font-semibold tabular-nums">{tips.distribution.shared}%</span>
                  </div>
                </div>
              </div>
            </Section>

            {taxes ? (
              <Section title={tSection("taxes_title")} href="/manage/location/taxes" testId="branch-edit-taxes">
                {taxes.rates.length === 0 ? (
                  <div className="space-y-3" data-testid="branch-taxes-empty">
                    <p className="flex items-center gap-2 text-sm font-medium">
                      <span className="size-2 rounded-full bg-warning" />
                      {tShow("taxes_none")}
                    </p>
                    <p className="text-xs text-muted-foreground">{tShow("taxes_none_hint")}</p>
                    <Button asChild variant="outline" size="sm">
                      <Link href="/manage/location/taxes">{tShow("taxes_configure")}</Link>
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="branch-taxes">
                    <div>
                      {taxes.rates.map((rate) => (
                        <SummaryRow key={rate.name} label={rate.name}>
                          <span className="tabular-nums">{rate.rate}%</span>
                        </SummaryRow>
                      ))}
                    </div>
                    <p className="border-t pt-3 text-xs text-muted-foreground">
                      {taxes.regimeLabel} ·{" "}
                      {taxes.pricesIncludeTax ? tShow("taxes_included") : tShow("taxes_added")}
                    </p>
                  </div>
                )}
              </Section>
            ) : null}

            <Section title={tShow("profile_title")} href="/manage/location/edit" testId="branch-edit-profile">
              <div data-testid="branch-profile">
                <SummaryRow label={tShow("profile_description")}>
                  {location.description || <NotSet />}
                </SummaryRow>
                <SummaryRow label={tShow("profile_address")}>
                  {location.addressLine1 || <NotSet />}
                </SummaryRow>
                <SummaryRow label={tShow("profile_city")}>
                  {[location.city, location.state].filter(Boolean).join(", ") || <NotSet />}
                </SummaryRow>
                <SummaryRow label={tShow("profile_coordinates")}>
                  {location.hasCoordinates ? tShow("coordinates_present") : <NotSet />}
                </SummaryRow>
                <SummaryRow label={tShow("profile_phone")}>
                  {location.supportPhone ? (
                    <span className="tabular-nums">{location.supportPhone}</span>
                  ) : (
                    <NotSet />
                  )}
                </SummaryRow>
                <SummaryRow label={tShow("profile_logo")}>
                  {location.logoUrl ? tShow("logo_present") : <NotSet label={tShow("logo_missing")} />}
                </SummaryRow>
              </div>
            </Section>
          </div>
        </div>
      </MainContainer>
    </StaffLayout>
  )
}

function FeatureList({
  label,
  features,
  enabled = false,
}: {
  label: string
  features: FeatureName[]
  enabled?: boolean
}) {
  if (features.length === 0) return null

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="flex flex-wrap gap-2">
        {features.map((feature) => (
          <span
            key={feature.key}
            data-testid={`branch-feature-${feature.key}`}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium",
              enabled ? "border-border" : "border-dashed text-muted-foreground"
            )}
          >
            {enabled && <span className="size-2 rounded-full bg-status-available" />}
            {feature.label}
          </span>
        ))}
      </div>
    </div>
  )
}
